import { Injectable, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Role } from '../models/user-role';
import { GlobalApps, GlobalChildApps, GlobalConfigComponents, GlobalVariable } from '../helpers/settings';
import { Result } from '../models/general';
import { Project } from '../models/project';
import { cloneDeep } from 'lodash';
import { UserService } from './user.service';
import { Subscription } from '../models/subscription';
import { RefreshVerificationService } from './refresh-verification.service';

@Injectable({
	providedIn: 'root'
})
export class RoleService implements OnInit {
	readonly BASE_URL = `${GlobalVariable.server.host}/users-roles`;
	public globalVariables: any = GlobalVariable;

	constructor(
		private httpClient: HttpClient,
		private userService: UserService,
		private refreshVerifService: RefreshVerificationService
	) {}

	ngOnInit(): void {}

	createRole(
		role: Partial<Role>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<{ done: boolean; message?: string }> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.post<Result>(`${this.BASE_URL}/create`, role, { params })
		);
	}

	findByRole(
		role: Role,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		action: string
	): Observable<Partial<Role>> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName)
			.set('actionName', action);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Partial<Role>>(`${this.BASE_URL}/findRole/${role.name}`, { params })
		);
	}

	findById(
		id: string,
		isChildComponent: boolean,
		fromConfigModelComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		route,
		projectId: string,
		action: string
	): Observable<Role> {
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('actionName', action);
		}
		if (fromConfigModelComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigModelComponent)
				.set('componentRoute', route)
				.set('actionName', action);
		} else if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId)
				.set('actionName', action);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Role>(`${this.BASE_URL}/find-by-id/${id}`, { params })
		);
	}

	findRoles(
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		action: string
	): Observable<Partial<Role[]>> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName)
			.set('actionName', action);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Partial<Role[]>>(`${this.BASE_URL}/find`, { params })
		);
	}
	findRolesBuUser(
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		action: string
	): Observable<Partial<Role[]>> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName)
			.set('actionName', action);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Partial<Role[]>>(`${this.BASE_URL}/find-by-user`, { params })
		);
	}

	edit(
		role: Partial<Role>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.put<Result>(`${this.BASE_URL}/edit`, role, { params })
		);
	}

	delete(
		roleId: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/delete/${roleId}`, { params })
		);
	}

	public initDefaultComponentRoles(projects: Project[]): Role {
		let accessComponents = this.initDefaultConfigAccess();
		if (accessComponents.length) {
			const user = this.userService.getCurrentUserInfos();
			const currentSubscription = user?.subscription as Subscription;
			const activatedApps = GlobalApps.filter(app => currentSubscription?.applications?.includes(app.code));
			const dashboardComponent = Object.values(this.globalVariables).find(
				(c: any) => !Array.isArray(c) && c.isComponent && c.route === 'dashboard'
			);
			let a = Object.values(activatedApps).filter(
				(c: any) => !Array.isArray(c) && c.isComponent && (c.isApp || c.name === 'dashboard')
			);
			a.unshift(dashboardComponent);
			if (a.length > 0) {
				const componentAccess = a.map((value: any) => ({
					code: value?.code,
					name: value.name,
					route: value.route,
					title: value.title,
					isActive: false,
					actions: value?.actions?.map(actName => ({
						name: actName,
						isActive: false
					})),
					childComponents: value?.childApps?.length
						? value?.childApps?.map(childApp => ({
								code: this.globalVariables[childApp]?.code,
								name: this.globalVariables[childApp]?.name,
								route: this.globalVariables[childApp]?.route,
								title: this.globalVariables[childApp]?.title,
								isActive: false,
								actions: this.globalVariables[childApp].actions?.map(actName => ({
									name: actName,
									childRoute:
										this.globalVariables[childApp]?.childRoute.find(
											action => action?.actionName == actName
										)?.route ?? null,
									isActive: false
								}))
						  }))
						: [],
					childRoute: value?.childRoute
				}));
				for (const project of projects) {
					accessComponents.push({
						config: null,
						project: project._id,
						componentAccess: cloneDeep(componentAccess)
					});
				}
				return { name: '', access: accessComponents };
			} else {
				return { name: '', access: [] };
			}
		} else {
			return { name: '', access: [] };
		}
	}

	public initDefaultConfigAccess() {
		const user = this.userService.getCurrentUserInfos();
		const currentSubscription = user?.subscription as Subscription;
		let accessComponents = [];
		const a = GlobalConfigComponents.filter(configComponents => {
			if (!configComponents.isAdmin && !configComponents.isChildComponent) {
				if (configComponents.isModel) {
					return currentSubscription?.applications.includes(configComponents?.appCode);
				}
				return true;
			}
		});
		const componentAccess = a.map((value: any) => ({
			code: value.code,
			name: value.name,
			route: value.route,
			title: value.title,
			appCode: value.isModel && !value.isChildModel ? value.appCode : null,
			isActive: false,
			actions: value?.actions
				? value.actions?.map(actName => ({
						name: actName,
						childRoute: value?.childRoute?.find(action => action?.actionName == actName)?.route,
						isActive: false
				  }))
				: [],
			childComponents: value?.childComponents
				? value.childComponents?.map(childComponent => ({
						code: this.globalVariables[childComponent]?.code,
						name: this.globalVariables[childComponent]?.name,
						route: this.globalVariables[childComponent]?.route,
						title: this.globalVariables[childComponent]?.title,
						isActive: false,
						actions: this.globalVariables[childComponent].actions?.map(actName => ({
							name: actName,
							childRoute: this.globalVariables[childComponent]?.childRoute.find(
								action => action?.actionName == actName
							)?.route,
							isActive: false
						}))
				  }))
				: []
		}));
		accessComponents.push({ config: 'Config', project: null, componentAccess: cloneDeep(componentAccess) });
		return accessComponents;
	}

	getAuthorization(
		role: Role,
		config: string,
		projectId: string,
		componentRoute: string,
		actionName: string,
		globalComponent: string = null,
		childComponent: string = null
	): boolean {
		if (this.hasOwnerAccess()) return true; // for owner, admin and super admin
		const currentUserRole = role;
		if (projectId && !config) {
			const projectComponentAccess = currentUserRole?.access.find(access => access.project === projectId);
			const isExistedComp = projectComponentAccess.componentAccess.find(comptAccess =>
				globalComponent
					? comptAccess.code == this.globalVariables[globalComponent]?.code
					: comptAccess.route == componentRoute
			);
			if (isExistedComp && isExistedComp?.isActive) {
				if (isExistedComp.childComponents.length) {
					const isExisteChildCompt = isExistedComp.childComponents.find(
						childCmp => childCmp?.code == this.globalVariables[childComponent].code
					);

					if (isExisteChildCompt && isExisteChildCompt?.isActive) {
						const isExisteChildComptAct = isExisteChildCompt.actions.find(
							action => action?.name == actionName
						);

						if (isExisteChildComptAct && isExisteChildComptAct.isActive) {
							return true;
						} else {
							return false;
						}
					} else {
						return false;
					}
				} else {
					const isExistedCompAct = isExistedComp.actions.find(action => action?.name == actionName);
					if (isExistedCompAct && isExistedCompAct.isActive) {
						return true;
					} else {
						return false;
					}
				}
			} else {
				return false;
			}
		}
		if (config && !projectId) {
			const configComponentAccess = currentUserRole?.access.find(access => access.config === config);

			const isExistedComp = configComponentAccess.componentAccess.find(
				comptAccess => comptAccess.name == this.globalVariables[globalComponent]?.name
			);

			if (isExistedComp && isExistedComp?.isActive) {
				if (isExistedComp.childComponents.length) {
					const isExisteChildCompt = isExistedComp.childComponents.find(
						childCmp => childCmp?.name == this.globalVariables[childComponent].name
					);

					if (isExisteChildCompt && isExisteChildCompt?.isActive) {
						const isExisteChildComptAct = isExisteChildCompt.actions.find(
							action => action?.name == actionName
						);

						if (isExisteChildComptAct && isExisteChildComptAct.isActive) {
							return true;
						} else {
							return false;
						}
					} else {
						return false;
					}
				} else {
					const isExistedCompAct = isExistedComp.actions.find(action => action?.name == actionName);

					if (isExistedCompAct && isExistedCompAct.isActive) {
						return true;
					} else {
						return false;
					}
				}
			} else {
				return false;
			}
		}
	}

	getAuthorizationProjectComponent(
		role: Role,
		projectId: string,
		componentRoute: string,
		actionFromRoute: string,
		parentComponentRoute?: string
	) {
		const currentUserRole = role;
		const isAppComponent = GlobalApps?.find(appComponent => appComponent.route == componentRoute);
		const projectComponentAccess = currentUserRole?.access.find(access => access.project === projectId);
		const componentAccess = projectComponentAccess?.componentAccess.find(
			componentAccess => componentAccess.route === componentRoute
		);
		// Component with Child Actions
		if (isAppComponent && isAppComponent.hasChildActions && actionFromRoute) {
			if (componentAccess?.isActive) {
				let hasAccess = false;
				const childRoute = isAppComponent?.childRoute?.find(childRoute => childRoute.route == actionFromRoute);
				if (childRoute) {
					const actionAccess = componentAccess?.actions.find(
						actionAccess => actionAccess.name === childRoute.actionName
					);
					if (actionAccess?.isActive && actionFromRoute == childRoute.route) {
						hasAccess = true;
					}
				}
				return hasAccess;
			} else {
				return false;
			}
		}
		if (componentAccess?.isActive) {
			return true;
		} else {
			return false;
		}
	}

	getAuthorizationProjectHRComponent(
		role: Role,
		projectId: string,
		componentRoute: string,
		actionFromRoute: string,
		parentComponentRoute?: string
	) {
		const currentUserRole = role;
		const isAppComponent = GlobalChildApps?.find(appComponent => appComponent.route == componentRoute);
		const projectComponentAccess = currentUserRole?.access.find(access => access.project === projectId);
		const componentAccess = projectComponentAccess?.componentAccess.find(
			componentAccess => componentAccess.route === parentComponentRoute
		);
		if (componentAccess?.isActive) {
			if (componentAccess?.childComponents?.length) {
				const childComp = componentAccess.childComponents.find(
					childComp => childComp?.name == isAppComponent?.name
				);
				return childComp?.isActive;
			} else {
				return true;
			}
		} else {
			return false;
		}
	}

	getAuthorizationConfigComponent(role: Role, componentRoute: string, actionFromRoute: string) {
		const currentUserRole = role;
		const isConfigComponent = GlobalConfigComponents?.find(
			configComponent => configComponent.route == componentRoute
		);

		const configComponentAccess = currentUserRole?.access.find(access => access.config === 'Config');
		// Child Component
		if (isConfigComponent && (isConfigComponent.isChildComponent || isConfigComponent?.childComponents?.length)) {
			const componentAccess = configComponentAccess?.componentAccess.find(
				componentAccess =>
					componentAccess.route === isConfigComponent.parentComponentRoute ||
					componentAccess.route === isConfigComponent.route
			);
			if (componentAccess?.isActive) {
				if (actionFromRoute) {
					let hasAccess = false;

					const childComponentAction = isConfigComponent.childRoute?.find(
						childAction => childAction.route == actionFromRoute
					);

					if (childComponentAction) {
						let actionAccess = false;
						let childComponent = componentAccess?.childComponents.find(
							childComponent => childComponent.name == isConfigComponent.name
						);
						actionAccess = childComponent?.actions?.find(
							childCompAction => childCompAction.name === childComponentAction.actionName
						)?.isActive;
						if (actionAccess && actionFromRoute == childComponentAction.route) {
							hasAccess = true;
						}
					}
					return hasAccess;
				}
				return true;
			} else {
				return false;
			}
		}
		// Component with Child Actions
		if (
			isConfigComponent &&
			(isConfigComponent?.hasChildActions || !isConfigComponent?.childComponents?.length) &&
			actionFromRoute
		) {
			const componentAccess = configComponentAccess?.componentAccess.find(
				componentAccess => componentAccess.route === isConfigComponent.route
			);
			if (componentAccess?.isActive) {
				let hasAccess = false;
				const childRoute = isConfigComponent.childRoute.find(
					childAction => childAction.route == actionFromRoute
				);
				if (childRoute) {
					const actionAccess = componentAccess?.actions.find(
						actionAccess => actionAccess.name === childRoute.actionName
					);
					if (actionAccess?.isActive && actionFromRoute == childRoute.route) {
						hasAccess = true;
					}
				}
				return hasAccess;
			} else {
				return false;
			}
		}

		const componentAccess = configComponentAccess?.componentAccess.find(
			componentAccess => componentAccess.route === componentRoute
		);
		if (componentAccess?.isActive) {
			return true;
		} else {
			return false;
		}
	}

	getApplicationAuthorization(applicationRoute, currentSubscription) {
		const componentRouteUpperCase = this.toUpperCaseWithUnderscores(applicationRoute); // pour transformer par exemple du invoice-management en INVOICE_MANAGEMENT
		if (
			currentSubscription?._id &&
			(currentSubscription?.applications.includes(componentRouteUpperCase) || applicationRoute === 'dashboard')
		) {
			return true;
		} else {
			return false;
		}
	}
	toUpperCaseWithUnderscores(input: string): string {
		return input
			?.split('-')
			?.map(word => word.toUpperCase())
			?.join('_');
	}
	public hasOwnerAccess = (): boolean => {
		const roles = this.userService.getCurrentUser()?.roles;
		return roles?.some(role => ['Owner', 'Admin', 'SuperAdmin'].includes(role));
	};
	public hasSuperAdminAccess = (): boolean => {
		const roles = this.userService.getCurrentUser()?.roles;
		return roles?.some(role => ['SuperAdmin'].includes(role));
	};
}
